import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CalendarSubscription,
  CreateCalendarSubscriptionRequest,
  CreateHabitRequest,
  CreateProjectRequest,
  DayPlanDto,
  Habit,
  Project,
  SyncCalendarSubscriptionResponse,
  UpdateProjectRequest,
} from '@mrrmlab/shared-types';
import { useApiClient } from './api-client-context';
import { calendarKeys, dayPlannerKeys, habitKeys, projectKeys } from './query-keys';

export function useDayPlan() {
  const client = useApiClient();
  return useQuery<DayPlanDto>({
    queryKey: dayPlannerKeys.today(),
    queryFn: ({ signal }) => client.planner.today(signal),
    refetchOnWindowFocus: true,
  });
}

export function useRunPlanner() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<DayPlanDto>({
    mutationFn: () => client.planner.run(),
    onSuccess: (data) => {
      qc.setQueryData(dayPlannerKeys.today(), data);
    },
  });
}

// Optimistic Mark-Done (#464). The item disappears from /heute immediately;
// on error we roll back to the snapshot. Undo lives in the screen — we keep
// the previous snapshot in the mutation context so the toast can restore it.
export function useMarkCandidateDone() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, string, { previous: DayPlanDto | undefined }>({
    mutationFn: async (candidateId) => {
      await client.candidates.markDone(candidateId);
      return { id: candidateId };
    },
    onMutate: async (candidateId) => {
      await qc.cancelQueries({ queryKey: dayPlannerKeys.today() });
      const previous = qc.getQueryData<DayPlanDto>(dayPlannerKeys.today());
      if (previous) {
        qc.setQueryData<DayPlanDto>(dayPlannerKeys.today(), {
          ...previous,
          items: previous.items.filter((i) => i.candidateId !== candidateId),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(dayPlannerKeys.today(), ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dayPlannerKeys.today() });
    },
  });
}

export function useQuickTodo() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<DayPlanDto, Error, string>({
    mutationFn: (title) => client.planner.quickTodo(title),
    onSuccess: (data) => {
      qc.setQueryData(dayPlannerKeys.today(), data);
    },
  });
}

export function useCalendarSubscriptions() {
  const client = useApiClient();
  return useQuery<CalendarSubscription[]>({
    queryKey: calendarKeys.subscriptions(),
    queryFn: ({ signal }) => client.calendar.listSubscriptions(signal),
  });
}

export function useCreateCalendarSubscription() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<CalendarSubscription, Error, CreateCalendarSubscriptionRequest>({
    mutationFn: (body) => client.calendar.createSubscription(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.subscriptions() });
    },
  });
}

export function useDeleteCalendarSubscription() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.calendar.deleteSubscription(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.subscriptions() });
      qc.invalidateQueries({ queryKey: dayPlannerKeys.today() });
    },
  });
}

export function useSyncCalendarSubscription() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<SyncCalendarSubscriptionResponse, Error, string>({
    mutationFn: (id) => client.calendar.syncSubscription(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.subscriptions() });
      qc.invalidateQueries({ queryKey: dayPlannerKeys.today() });
    },
  });
}

export function useHabits() {
  const client = useApiClient();
  return useQuery<Habit[]>({
    queryKey: habitKeys.list(),
    queryFn: ({ signal }) => client.habits.list(signal),
  });
}

export function useCreateHabit() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<Habit, Error, CreateHabitRequest>({
    mutationFn: (body) => client.habits.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: habitKeys.list() });
    },
  });
}

export function useDeleteHabit() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.habits.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: habitKeys.list() });
    },
  });
}

export function useProjects() {
  const client = useApiClient();
  return useQuery<Project[]>({
    queryKey: projectKeys.list(),
    queryFn: ({ signal }) => client.projects.list(signal),
  });
}

export function useCreateProject() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<Project, Error, CreateProjectRequest>({
    mutationFn: (body) => client.projects.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.list() });
      qc.invalidateQueries({ queryKey: dayPlannerKeys.today() });
    },
  });
}

export function useUpdateProject() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<Project, Error, { id: string; body: UpdateProjectRequest }>({
    mutationFn: ({ id, body }) => client.projects.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

export function useDeleteProject() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.projects.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.list() });
      qc.invalidateQueries({ queryKey: dayPlannerKeys.today() });
    },
  });
}
